// ════════════════════════════════════════════════════════════════════
//  העדפות נגישות (תקנה 35 / IS 5568) — store עם pub-sub + localStorage.
//  כלי העדפות-משתמש בלבד: משנה רק קלאסים על <html>, לא נוגע בתוכן ה-DOM,
//  לא מזריק alt/aria. (הקו שמפריד כלי נגישות תקין מ-overlay אסור.)
// ════════════════════════════════════════════════════════════════════
import { useSyncExternalStore } from "react";
import { KEY, VERSION, DEFAULT_PREFS, CLASS_RULES, applyPrefsToElement } from "./a11yRules";

// re-export כדי שהצרכנים הקיימים לא ישתנו
export { KEY, VERSION, DEFAULT_PREFS, CLASS_RULES, applyPrefsToElement, A11Y_BOOTSTRAP_SCRIPT } from "./a11yRules";

function load() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_PREFS };
    const p = JSON.parse(raw);
    if (p.version !== VERSION) return { ...DEFAULT_PREFS };
    return { ...DEFAULT_PREFS, ...p };
  } catch {
    return { ...DEFAULT_PREFS };
  }
}

let state = typeof window !== "undefined" ? load() : { ...DEFAULT_PREFS };
const listeners = new Set();


function notify() {
  if (typeof document !== "undefined") applyPrefsToElement(state);
  listeners.forEach((l) => l());
}

export const a11yStore = {
  get: () => state,
  set: (patch) => {
    state = { ...state, ...patch, version: VERSION };
    try { localStorage.setItem(KEY, JSON.stringify(state)); } catch { /* ignore */ }
    notify();
  },
  reset: () => {
    state = { ...DEFAULT_PREFS };
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    notify();
  },
  subscribe: (l) => { listeners.add(l); return () => listeners.delete(l); },
};

// React hook
export function useA11yPrefs() {
  return useSyncExternalStore(a11yStore.subscribe, a11yStore.get, () => DEFAULT_PREFS);
}

// גלילה חלקה היא אנימציה שרצה ב-JS — CSS ‎scroll-behavior‎ לא חל על
// ‎window.scrollTo({behavior})‎, ולכן "עצירת אנימציות" חייבת לעבור גם כאן.
// מכבד גם את העדפת מערכת ההפעלה, לא רק את המתג בווידג'ט.
export function prefersNoMotion() {
  if (typeof window === "undefined") return false;
  if (state.reduceMotion) return true;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}

export function motionSafeScrollBehavior() {
  return prefersNoMotion() ? "auto" : "smooth";
}

// hook חי ל"עצירת אנימציות". אנימציות שמונעות ב-JS (setInterval / rAF)
// אינן מושפעות לא מ-CSS ולא מ-MotionGlobalConfig של framer, ולכן כל רכיב
// שמריץ לולאה כזאת חייב לשאול את ההעדפה בעצמו ולהציג מצב סופי סטטי.
export function useNoMotion() {
  const { reduceMotion } = useA11yPrefs();
  if (reduceMotion) return true;
  if (typeof window === "undefined") return false;
  try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; }
  catch { return false; }
}
